import { useEffect, useState, useCallback, useReducer, useMemo } from "react";
import { isFunction as _isFunction } from "lodash-es";

export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const valueDelay = _isFunction(delay) ? delay(value) : delay;

    if (valueDelay < 1) {
      setDebouncedValue(value);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, valueDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useToggle(initialValue) {
  const [enabled, setEnabled] = useState(initialValue);

  const toggle = useCallback(() => {
    setEnabled(!enabled);
  }, [enabled]);

  return [enabled, toggle, setEnabled];
}

export function useInfiniteListScroll(initialCount) {
  const reducer = useCallback(
    (state, action) => {
      switch (action.type) {
        case "bump":
          return { count: state.count + initialCount };
        case "reset":
          return { count: initialCount };
        default:
          throw new Error();
      }
    },
    [initialCount]
  );

  const [state, dispatch] = useReducer(reducer, { count: initialCount });

  const bump = useCallback(() => {
    dispatch({ type: "bump" });
  }, []);

  const onScroll = useCallback((event) => {
    const el = event.target;

    if (el.scrollTop < 50) {
      dispatch({ type: "reset" });
      return;
    }

    const diff = el.scrollHeight - el.clientHeight - el.scrollTop;

    if (diff < 100) {
      dispatch({ type: "bump" });
    }
  }, []);

  return [state.count, onScroll, bump];
}

export function useRandomId(prefix) {
  const id = useMemo(() => {
    return `__${prefix}_${Math.random().toString(36).substr(2)}`;
  }, [prefix]);

  return id;
}

export function useMouseEnterLeave() {
  const [entered, setEntered] = useState(false);

  const onMouseEnter = useCallback(() => {
    setEntered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setEntered(false);
  }, []);

  const callbackAttrs = useMemo(() => {
    return { onMouseEnter, onMouseLeave };
  }, [onMouseEnter, onMouseLeave]);

  return [entered, callbackAttrs];
}
